import type { MaybeRefOrGetter, Ref } from 'vue';
import { toValue, computed } from 'vue';
import type { SelectItem, SelectItemSeparator } from './types';
import useListHoverController from './use-list-hover-controller';

type BaseOptions<T extends SelectItem> = {
  items: MaybeRefOrGetter<Readonly<(T | SelectItemSeparator)[]>>;
  modelValue: MaybeRefOrGetter<T['id'][]> | T['id'][];
  onChange: (values: T['id'][], item?: T) => void;
  useKeyboardEvents?: Ref<boolean>;
};

/**
 * Хук для управления `Множественным` выбором элементов в списке.
 */

const useListMultipleSelect = <T extends Readonly<SelectItem>>({
  items,
  modelValue = [],
  onChange,
  useKeyboardEvents
}: BaseOptions<T>) => {
  const select = (item: T) => {
    const result = [...toValue(modelValue), item.id];
    onChange?.(result, item);
    return result;
  };

  const deselect = (item: T) => {
    const result = toValue(modelValue).filter((id) => id !== item.id);
    onChange(result, item);
    return result;
  };

  const toggle = (item: T) => {
    const isSelected = toValue(modelValue).includes(item.id);

    if (isSelected) {
      return deselect(item);
    }
    return select(item);
  };

  const clear = () => {
    onChange([]);
  };

  // User selection handling
  const { onHover, isHovered, hoveredIndex, hoveredFromKeyboard } = useListHoverController({
    items,
    toggle,
    listenKeyboard: useKeyboardEvents
  });

  // Data
  const selectableItems = computed(() => {
    return toValue(items).map((item: T | SelectItemSeparator, index: number) => ({
      ...item,
      userSelection: {
        selected: 'id' in item ? toValue(modelValue).includes(item.id) : false,
        hovered: isHovered(index)
      }
    }));
  });

  return {
    onHover,
    toggle,
    clear,
    selectableItems,
    hoveredIndex,
    hoveredFromKeyboard
  };
};

export default useListMultipleSelect;
